import { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useForm } from "react-hook-form";
import { z } from "zod";
import { zodResolver } from "@hookform/resolvers/zod";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Building2, HelpCircle, Loader2 } from "lucide-react";
import api from "@/utils/api";
import { toast } from "@/hooks/use-toast";
import {
  setCfcData,
  setCurrentTab,
  setUserInitiatedNav,
} from "../../../store/slices/cfcSlice";
import { useLocation, useNavigate } from "react-router-dom";
import FilePreview from "../../../components/common/FilePreview";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import { Separator } from "@/components/ui/separator";
import { MultiSelect } from "../../../components/ui/multiselect";
import purchase_certificate from "../../../assets/documents/purchase_certificate.pdf";
import sampleImage from "../../../assets/sample_gps_image.jpeg";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "@/components/ui/dialog";

const isValidFile = (file) =>
  file instanceof File || (typeof file === "string" && file.length > 0);

const spvSchema = z
  .object({
    spv_name: z.string().min(1, "SPV Name is required"),
    spv_address: z.string().min(1, "SPV Address is required"),
    spv_contact_number: z.string().min(1, "SPV Contact Number is required"),
    spv_pan: z
      .string()
      .min(1, "PAN is required")
      .refine(
        (val) => !val || /^[A-Z]{5}[0-9]{4}[A-Z]{1}$/.test(val),
        "Invalid PAN Number"
      ),
    spv_tan: z
      .string()
      .min(1, "TAN is required")
      .refine(
        (val) => !val || /^[A-Z]{4}\d{5}[A-Z]{1}$/.test(val),
        "Invalid TAN Number"
      ),
    spv_gst: z
      .string()
      .min(1, "GST is required")
      .refine(
        (val) =>
          !val ||
          /^[0-9]{2}[A-Z]{5}[0-9]{4}[A-Z]{1}[1-9A-Z]{1}Z[0-9A-Z]{1}$/.test(val),
        "Invalid GST Number"
      ),
    registration_details: z.string().min(1, "Registration Proof is required"),
    formation_date: z.string().optional(),
    business_commencement_date: z.string().optional(),
    spv_members: z.string().optional(),
    bye_laws: z.string().min(1, "Bye laws selection is required"),
    main_objectives: z.string().optional(),
    district_id: z.string().min(1, "District is required"),
    spv_city: z.string().min(1, "City is required"),
    spv_state: z.string().min(1, "State is required"),
    spv_pin_code: z
      .string()
      .min(1, "Pincode is required")
      .regex(/^[0-9]{6}$/, "Pincode must be 6 digits"),
    spv_pan_copy: z
      .any()
      .refine((file) => isValidFile(file), "PAN upload is required"),
    spv_tan_copy: z
      .any()
      .refine((file) => isValidFile(file), "TAN upload is required"),
    spv_gst_copy: z
      .any()
      .refine((file) => isValidFile(file), "GST upload is required"),
    registration_copy: z
      .any()
      .refine((file) => isValidFile(file), "Registration upload is required"),
    members_aadhaar_copy: z
      .any()
      .refine(
        (file) => isValidFile(file),
        "Members aadhaar upload is required"
      ),
    purchase_detail_bond: z
      .any()
      .refine((file) => isValidFile(file), "This document is required"),
    bye_laws_copy: z.any().optional(),
    geo_tag: z.any().refine((file) => isValidFile(file), "Geo Tag is required"),
    main_facilities_proposed: z
      .array(z.string())
      .min(1, "At least one Facilities to be Created must be selected"),
    cluster_details: z
      .string()
      .min(1, "Proposed CFC located in cluster must be selected"),
    about_cluster_copy: z.any().optional(),
  })
  .superRefine((data, ctx) => {
    if (data?.bye_laws == "yes") {
      if (!isValidFile(data.bye_laws_copy)) {
        ctx.addIssue({
          path: ["bye_laws_copy"],
          code: z.ZodIssueCode.custom,
          message: "Bye laws or MoA and AoA attachment is required",
        });
      }
    }
    if (data?.cluster_details == "yes") {
      if (!isValidFile(data.about_cluster_copy)) {
        ctx.addIssue({
          path: ["about_cluster_copy"],
          code: z.ZodIssueCode.custom,
          message: "Attachment is required",
        });
      }
    }
  });

const SpvDetails = ({ data, districts }) => {
  const dispatch = useDispatch();
  const location = useLocation();
  const nav = useNavigate();
  const [filePreviews, setFilePreviews] = useState({});
  const [districtNoteOpen, setDistrictNoteOpen] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [buttonType, setButtonType] = useState("submit");
  const {
    register,
    handleSubmit,
    setValue,
    watch,
    setFocus,
    formState: { errors },
    trigger,
  } = useForm({
    resolver: zodResolver(spvSchema),
    defaultValues: {
      spv_name: data?.spv_name || "",
      spv_address: data?.spv_address || "",
      spv_contact_number: data?.spv_contact_number || "",
      spv_pan: data?.spv_pan || "",
      spv_tan: data?.spv_tan || "",
      spv_gst: data?.spv_gst || "",
      district_id: data?.district_id ? String(data.district_id) : "",
      spv_city: data?.spv_city || "",
      spv_state: data?.spv_state || "Tamilnadu",
      spv_pin_code: data?.spv_pin_code ?? "",
      registration_details: data?.registration_details || "",
      formation_date: data?.formation_date
        ? new Date(data?.formation_date).toISOString().split("T")[0]
        : "",
      business_commencement_date: data?.business_commencement_date
        ? new Date(data?.business_commencement_date).toISOString().split("T")[0]
        : "",
      spv_members: data?.spv_members || "",
      bye_laws: data?.bye_laws || "",
      main_objectives: data?.main_objectives || "",
      project_specific_account: data?.project_specific_account || "",
      utilization_capacity: data?.utilization_capacity || "",
      main_facilities_proposed: Array.isArray(data?.main_facilities_proposed)
        ? data.main_facilities_proposed
        : [],
      cluster_details: data?.cluster_details || "",
      spv_pan_copy: data?.spv_pan_copy || "",
      spv_tan_copy: data?.spv_tan_copy || "",
      spv_gst_copy: data?.spv_gst_copy || "",
      registration_copy: data?.registration_copy || "",
      members_aadhaar_copy: data?.members_aadhaar_copy || "",
      purchase_detail_bond: data?.purchase_detail_bond || "",
      bye_laws_copy: data?.bye_laws_copy || "",
      geo_tag: data?.geo_tag || "",
      basic_cluster_data_copy: data?.basic_cluster_data_copy || "",
      about_cluster_copy: data?.about_cluster_copy || "",
    },
  });

  useEffect(() => {
    if (data) {
      const previews = {
        spv_pan_copy: data?.spv_pan_copy || "",
        spv_tan_copy: data?.spv_tan_copy || "",
        spv_gst_copy: data?.spv_gst_copy || "",
        registration_copy: data?.registration_copy || "",
        members_aadhaar_copy: data?.members_aadhaar_copy || "",
        purchase_detail_bond: data?.purchase_detail_bond || "",
        bye_laws_copy: data?.bye_laws_copy || "",
        geo_tag: data?.geo_tag || "",
        basic_cluster_data_copy: data?.basic_cluster_data_copy || "",
        about_cluster_copy: data?.about_cluster_copy || "",
      };

      setFilePreviews(previews);
    }
  }, [data]);

  const formData = watch();

  const handleInputChange = async (name, value) => {
    setValue(name, value, { shouldValidate: true });

    if (name === "spv_pin_code") {
      const cleaned = value.replace(/\D/g, "").slice(0, 6);
      setValue("spv_pin_code", cleaned, { shouldValidate: true });
    }
  };

  const onSubmit = async (formData) => {
    try {
      setIsSubmitting(true);
      const searchParams = new URLSearchParams(location?.search);
      const submitData = new FormData();
      Object.keys(formData).forEach((key) => {
        const value = formData[key];

        if (value instanceof File) {
          submitData.append(key, value);
        } else if (Array.isArray(value)) {
          submitData.append(key, JSON.stringify(value));
        } else if (value !== undefined && value !== null && value !== "") {
          submitData.append(key, value.toString());
        }
      });

      if (data?.id) {
        submitData.append("id", data.id.toString());
      }

      const response = await api.post(
        "/cfc-subsidies/spv_details",
        submitData,
        {
          headers: {
            "Content-Type": "multipart/form-data",
          },
        }
      );

      dispatch(setCfcData(response?.data?.data || {}));

      toast({
        title: "Success",
        description: "SPV details submitted successfully",
        variant: "success",
      });

      searchParams.delete("tab");
      nav(
        {
          pathname: location.pathname,
          search: searchParams.toString(),
        },
        { replace: true }
      );

      dispatch(setUserInitiatedNav(true));
      if (buttonType == "next") {
        dispatch(setCurrentTab("eligibility"));
      }
    } catch (error) {
      console.error("Submission error:", error);
      toast({
        title: "Submission Failed",
        description:
          error.response?.data?.message || "Failed to submit application",
        variant: "destructive",
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleFileUpload = async (name, file) => {
    if (file) {
      setValue(name, file, { shouldValidate: true });

      if (file.type.startsWith("image/")) {
        const reader = new FileReader();
        reader.onload = (e) => {
          setFilePreviews((prev) => ({ ...prev, [name]: e.target.result }));
        };
        reader.readAsDataURL(file);
      }

      await trigger(name);
    }
  };

  const handleRemoveFile = (fieldName) => {
    setValue(fieldName, undefined, { shouldValidate: true });
    setFilePreviews((prev) => ({ ...prev, [fieldName]: null }));
  };

  const handleDownload = (fileUrl, fileName) => {
    const link = document.createElement("a");
    link.href = fileUrl;
    link.download = fileName;
    link.click();
  };

  return (
    <form onSubmit={handleSubmit(onSubmit)} noValidate>
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Building2 className="h-5 w-5" />
            Information about SPV (Special Purpose Vehicle)
          </CardTitle>
        </CardHeader>

        <CardContent className="space-y-6 mt-5" style={{ marginTop: "15px" }}>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label htmlFor="spv_name">
                SPV Name <span className="text-red-500">*</span>
              </Label>
              <Textarea
                id="spv_name"
                value={formData.spv_name}
                onChange={(e) => handleInputChange("spv_name", e.target.value)}
                className={errors.spv_name ? "border-red-500" : ""}
                rows={2}
              />
              {errors.spv_name && (
                <p className="text-sm text-red-500">
                  {errors.spv_name.message}
                </p>
              )}
            </div>

            <div className="space-y-2">
              <Label htmlFor="spv_address">
                SPV Address <span className="text-red-500">*</span>
              </Label>
              <Textarea
                id="spv_address"
                value={formData.spv_address}
                onChange={(e) =>
                  handleInputChange("spv_address", e.target.value)
                }
                className={errors.spv_address ? "border-red-500" : ""}
                rows={2}
              />

              {errors.spv_address && (
                <p className="text-sm text-red-500">
                  {errors.spv_address.message}
                </p>
              )}
            </div>

            <div className="space-y-2">
              <Label htmlFor="spv_contact_number">
                SPV Contact Number <span className="text-red-500">*</span>
              </Label>
              <Input
                id="spv_contact_number"
                type="tel"
                value={formData.spv_contact_number}
                onChange={(e) => {
                  const value = e.target.value.replace(/\D/g, "");
                  if (value.length <= 10) {
                    handleInputChange("spv_contact_number", value);
                  }
                }}
                className={errors.spv_contact_number ? "border-red-500" : ""}
                maxLength={10}
                placeholder="Enter 10-digit contact number"
              />
              {errors.spv_contact_number && (
                <p className="text-sm text-red-500">
                  {errors.spv_contact_number.message}
                </p>
              )}
            </div>

            <div className="space-y-2">
              <Label htmlFor="district_id" required>
                Select District
              </Label>
              <Select
                value={formData.district_id}
                onValueChange={(value) => {
                  if (value == "19") {
                    setDistrictNoteOpen(true);
                  }
                  setValue("district_id", value, { shouldValidate: true });
                }}
              >
                <SelectTrigger
                  className={errors.district_id ? "border-red-500" : ""}
                >
                  <SelectValue placeholder="Select District" />
                </SelectTrigger>
                <SelectContent>
                  {districts &&
                    districts.map((district) => (
                      <SelectItem key={district.id} value={String(district.id)}>
                        {district.name || `District ${district.id}`}
                      </SelectItem>
                    ))}
                </SelectContent>
              </Select>
              {errors.district_id && (
                <p className="text-sm text-red-500">
                  {errors.district_id.message}
                </p>
              )}
            </div>

            <div className="space-y-2">
              <Label htmlFor="spv_city">
                City <span className="text-red-500">*</span>
              </Label>
              <Input
                id="spv_city"
                {...register("spv_city")}
                onChange={(e) => handleInputChange("spv_city", e.target.value)}
                className={errors.spv_city ? "border-red-500" : ""}
              />
              {errors.spv_city && (
                <p className="text-sm text-red-500">
                  {errors.spv_city.message}
                </p>
              )}
            </div>

            <div className="space-y-2">
              <Label htmlFor="spv_state">
                State <span className="text-red-500">*</span>
              </Label>
              <Input
                id="spv_state"
                readOnly
                {...register("spv_state")}
                onChange={(e) => handleInputChange("spv_state", e.target.value)}
                className={
                  errors.spv_state
                    ? "border-red-500"
                    : "bg-muted cursor-not-allowed"
                }
                style={{ pointerEvents: "none" }}
              />
              {errors.spv_state && (
                <p className="text-sm text-red-500">
                  {errors.spv_state.message}
                </p>
              )}
            </div>

            <div className="space-y-2">
              <Label htmlFor="spv_pin_code">
                Pincode <span className="text-red-500">*</span>
              </Label>
              <Input
                id="spv_pin_code"
                {...register("spv_pin_code")}
                onChange={(e) =>
                  handleInputChange("spv_pin_code", e.target.value)
                }
                className={errors.pincode ? "border-red-500" : ""}
                placeholder="6-digit pincode"
              />
              {errors.pincode && (
                <p className="text-sm text-red-500">{errors.pincode.message}</p>
              )}
            </div>
          </div>

          <Separator />

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label htmlFor="spv_pan" required>
                PAN Number
              </Label>
              <Input
                id="spv_pan"
                value={formData.spv_pan}
                onChange={(e) => handleInputChange("spv_pan", e.target.value)}
                className={errors.spv_pan ? "border-red-500" : ""}
              />
              {errors.spv_pan && (
                <p className="text-sm text-red-500">{errors.spv_pan.message}</p>
              )}
            </div>

            {/* PAN Uploads with View Document Design */}
            <div className="space-y-2">
              <Label required>Upload PAN Documents</Label>
              <div className="border-2 border-dashed rounded-lg p-4 text-center">
                <Input
                  id="spv_pan_copy"
                  type="file"
                  accept=".pdf"
                  onChange={(e) =>
                    handleFileUpload("spv_pan_copy", e.target.files[0])
                  }
                  className={errors.spv_pan_copy ? "border-red-500" : ""}
                />

                <FilePreview
                  fieldName="spv_pan_copy"
                  preview={filePreviews.spv_pan_copy}
                  file={
                    formData.spv_pan_copy instanceof File
                      ? formData.spv_pan_copy
                      : null
                  }
                  existingFile={
                    typeof filePreviews.spv_pan_copy === "string"
                      ? filePreviews.spv_pan_copy
                      : null
                  }
                  onRemove={handleRemoveFile}
                />

                {errors.spv_pan_copy && (
                  <p className="text-sm text-red-500">
                    {errors.spv_pan_copy.message}
                  </p>
                )}
                <p className="ms-1 text-[12px] text-muted-foreground">
                  Supported only PDF Files max size 2MB
                </p>
              </div>
            </div>

            <div className="space-y-2">
              <Label htmlFor="spv_tan" required>
                TAN Number
              </Label>
              <Input
                id="spv_tan"
                value={formData.spv_tan}
                onChange={(e) => handleInputChange("spv_tan", e.target.value)}
                className={errors.spv_tan ? "border-red-500" : ""}
              />

              {errors.spv_tan && (
                <p className="text-sm text-red-500">{errors.spv_tan.message}</p>
              )}
            </div>

            {/* TAN Uploads with View Document Design */}
            <div className="space-y-2">
              <Label required>Upload TAN Documents</Label>
              <div className="border-2 border-dashed rounded-lg p-4 text-center">
                <Input
                  id="spv_tan_copy"
                  type="file"
                  accept=".pdf"
                  onChange={(e) =>
                    handleFileUpload("spv_tan_copy", e.target.files[0])
                  }
                  className={errors.spv_tan_copy ? "border-red-500" : ""}
                />

                <FilePreview
                  fieldName="spv_tan_copy"
                  preview={filePreviews.spv_tan_copy}
                  file={
                    formData.spv_tan_copy instanceof File
                      ? formData.spv_tan_copy
                      : null
                  }
                  existingFile={
                    typeof filePreviews.spv_tan_copy === "string"
                      ? filePreviews.spv_tan_copy
                      : null
                  }
                  onRemove={handleRemoveFile}
                />

                {errors.spv_tan_copy && (
                  <p className="text-sm text-red-500">
                    {errors.spv_tan_copy.message}
                  </p>
                )}
                <p className="ms-1 text-[12px] text-muted-foreground">
                  Supported only PDF Files max size 2MB
                </p>
              </div>
            </div>

            <div className="space-y-2">
              <Label htmlFor="spv_gst" required>
                GST Number
              </Label>
              <Input
                id="spv_gst"
                value={formData.spv_gst}
                onChange={(e) => handleInputChange("spv_gst", e.target.value)}
                className={errors.spv_gst ? "border-red-500" : ""}
              />
              {errors.spv_gst && (
                <p className="text-sm text-red-500">{errors.spv_gst.message}</p>
              )}
            </div>

            {/* GST Uploads with View Document Design */}
            <div className="space-y-2">
              <Label required>Upload GST Documents</Label>
              <div className="border-2 border-dashed rounded-lg p-4 text-center">
                <Input
                  id="spv_gst_copy"
                  type="file"
                  accept=".pdf"
                  onChange={(e) =>
                    handleFileUpload("spv_gst_copy", e.target.files[0])
                  }
                  className={errors.spv_gst_copy ? "border-red-500" : ""}
                />

                <FilePreview
                  fieldName="spv_gst_copy"
                  preview={filePreviews.spv_gst_copy}
                  file={
                    formData.spv_gst_copy instanceof File
                      ? formData.spv_gst_copy
                      : null
                  }
                  existingFile={
                    typeof filePreviews.spv_gst_copy === "string"
                      ? filePreviews.spv_gst_copy
                      : null
                  }
                  onRemove={handleRemoveFile}
                />

                {errors.spv_gst_copy && (
                  <p className="text-sm text-red-500">
                    {errors.spv_gst_copy.message}
                  </p>
                )}
                <p className="ms-1 text-[12px] text-muted-foreground">
                  Supported only PDF Files max size 2MB
                </p>
              </div>
            </div>

            <div className="space-y-2">
              <Label htmlFor="registration_details" required>
                Registration details of SPV
              </Label>
              <Input
                id="registration_details"
                value={formData.registration_details}
                onChange={(e) =>
                  handleInputChange("registration_details", e.target.value)
                }
                className={errors.registration_details ? "border-red-500" : ""}
              />

              {errors.registration_details && (
                <p className="text-sm text-red-500">
                  {errors.registration_details.message}
                </p>
              )}
            </div>

            {/* Registration Uploads with View Document Design */}
            <div className="space-y-2">
              <Label required>Upload Registration Document</Label>
              <div className="border-2 border-dashed rounded-lg p-4 text-center">
                <Input
                  id="registration_copy"
                  type="file"
                  accept=".pdf"
                  onChange={(e) =>
                    handleFileUpload("registration_copy", e.target.files[0])
                  }
                  className={errors.registration_copy ? "border-red-500" : ""}
                />

                <FilePreview
                  fieldName="registration_copy"
                  preview={filePreviews.registration_copy}
                  file={
                    formData.registration_copy instanceof File
                      ? formData.registration_copy
                      : null
                  }
                  existingFile={
                    typeof filePreviews.registration_copy === "string"
                      ? filePreviews.registration_copy
                      : null
                  }
                  onRemove={handleRemoveFile}
                />

                {errors.registration_copy && (
                  <p className="text-sm text-red-500">
                    {errors.registration_copy.message}
                  </p>
                )}
                <p className="ms-1 text-[12px] text-muted-foreground">
                  Supported only PDF Files max size 2MB
                </p>
              </div>
            </div>
          </div>

          <Separator />

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label htmlFor="formation_date">
                Date of formation of the company
              </Label>
              <Input
                id="formation_date"
                type="date"
                value={formData.formation_date}
                onChange={(e) =>
                  handleInputChange("formation_date", e.target.value)
                }
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="business_commencement_date">
                Date of commencement of business
              </Label>
              <Input
                id="business_commencement_date"
                type="date"
                value={formData.business_commencement_date}
                onChange={(e) =>
                  handleInputChange(
                    "business_commencement_date",
                    e.target.value
                  )
                }
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="bye_laws" required>
                Bye laws or MoA and AoA Documents{" "}
              </Label>
              <Select
                value={formData.bye_laws}
                onValueChange={(value) => handleInputChange("bye_laws", value)}
              >
                <SelectTrigger>
                  <SelectValue placeholder="Select Yes or No" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="yes">Yes</SelectItem>
                  <SelectItem value="no">No</SelectItem>
                </SelectContent>
              </Select>
              {errors.bye_laws && (
                <p className="text-sm text-red-500">
                  {errors.bye_laws.message}
                </p>
              )}
            </div>

            {/* Bye Laws Uploads with View Document Design */}
            <div className="space-y-2">
              <Label required={formData.bye_laws && formData.bye_laws == "yes"}>
                Bye laws or MoA and AoA Uploads
              </Label>
              <div className="border-2 border-dashed rounded-lg p-4 text-center">
                <Input
                  id="bye_laws_copy"
                  type="file"
                  accept=".pdf"
                  onChange={(e) =>
                    handleFileUpload("bye_laws_copy", e.target.files[0])
                  }
                  className={errors.bye_laws_copy ? "border-red-500" : ""}
                />

                <FilePreview
                  fieldName="bye_laws_copy"
                  preview={filePreviews.bye_laws_copy}
                  file={
                    formData.bye_laws_copy instanceof File
                      ? formData.bye_laws_copy
                      : null
                  }
                  existingFile={
                    typeof filePreviews.bye_laws_copy === "string"
                      ? filePreviews.bye_laws_copy
                      : null
                  }
                  onRemove={handleRemoveFile}
                />

                {errors.bye_laws_copy && (
                  <p className="text-sm text-red-500">
                    {errors.bye_laws_copy.message}
                  </p>
                )}
                <p className="ms-1 text-[12px] text-muted-foreground">
                  Supported only PDF Files max size 2MB
                </p>
              </div>
            </div>

            <div className="space-y-2">
              <Label htmlFor="spv_members">Name of the Members in SPV</Label>
              <Textarea
                id="spv_members"
                value={formData.spv_members}
                onChange={(e) =>
                  handleInputChange("spv_members", e.target.value)
                }
                rows={3}
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="main_objectives">
                Main objectives of the SPV
              </Label>
              <Textarea
                id="main_objectives"
                value={formData.main_objectives}
                onChange={(e) =>
                  handleInputChange("main_objectives", e.target.value)
                }
                rows={3}
              />
            </div>

            {/* Members Aadhaar Uploads with View Document Design */}
            <div className="space-y-2">
              <Label required>
                Uploads Members Aadhaar documents
                <br /> (Upto 5 Members can merge the aadhaar in single file)
              </Label>
              <div className="border-2 border-dashed rounded-lg p-4 text-center">
                <Input
                  id="members_aadhaar_copy"
                  type="file"
                  accept=".pdf"
                  onChange={(e) =>
                    handleFileUpload("members_aadhaar_copy", e.target.files[0])
                  }
                  className={
                    errors.members_aadhaar_copy ? "border-red-500" : ""
                  }
                />

                <FilePreview
                  fieldName="members_aadhaar_copy"
                  preview={filePreviews.members_aadhaar_copy}
                  file={
                    formData.members_aadhaar_copy instanceof File
                      ? formData.members_aadhaar_copy
                      : null
                  }
                  existingFile={
                    typeof filePreviews.members_aadhaar_copy === "string"
                      ? filePreviews.members_aadhaar_copy
                      : null
                  }
                  onRemove={handleRemoveFile}
                />

                {errors.members_aadhaar_copy && (
                  <p className="text-sm text-red-500">
                    {errors.members_aadhaar_copy.message}
                  </p>
                )}
                <p className="ms-1 text-[12px] text-muted-foreground">
                  Supported only PDF Files max size 10MB
                </p>
              </div>
            </div>

            <div className="space-y-2">
              <Label>
                Undertaking certificate for not selling the Component for 5
                years <span className="text-red-500">*</span>
                <Button
                  type="button"
                  variant="link"
                  onClick={() =>
                    handleDownload(
                      purchase_certificate,
                      `purchase_certificate.pdf`
                    )
                  }
                  className="text-wrap text-blue-500"
                >
                  (Download Undertaking Certificate <br />
                  and print in 20 rupees stamp paper )
                </Button>
              </Label>
              <div className="border-2 border-dashed rounded-lg p-4 text-center">
                <Input
                  id="purchase_detail_bond"
                  type="file"
                  accept=".pdf"
                  onChange={(e) =>
                    handleFileUpload("purchase_detail_bond", e.target.files[0])
                  }
                  className={
                    errors.purchase_detail_bond ? "border-red-500" : ""
                  }
                />

                <FilePreview
                  fieldName="purchase_detail_bond"
                  preview={filePreviews.purchase_detail_bond}
                  file={
                    formData.purchase_detail_bond instanceof File
                      ? formData.purchase_detail_bond
                      : null
                  }
                  existingFile={
                    typeof filePreviews.purchase_detail_bond === "string"
                      ? filePreviews.purchase_detail_bond
                      : null
                  }
                  onRemove={handleRemoveFile}
                />

                {errors.purchase_detail_bond && (
                  <p className="text-sm text-red-500">
                    {errors.purchase_detail_bond.message}
                  </p>
                )}
                <p className="ms-1 text-[12px] text-muted-foreground">
                  Supported only PDF Files max size 10MB
                </p>
              </div>
            </div>

            {/* Location of CFC - Geo Tag with View Document Design */}
            <div className="space-y-2">
              <Label
                htmlFor="geo_tag"
                required
                className="flex items-center gap-1"
              >
                <span>Location of CFC - GPS Location</span>
                <Popover>
                  <PopoverTrigger>
                    <HelpCircle className="text-primary p-0 m-0" />
                  </PopoverTrigger>
                  <PopoverContent>
                    Install the Geo tag application in your android device{" "}
                    <a
                      href="https://play.google.com/store/apps/details?id=com.gpsmapcamera.geotagginglocationonphoto"
                      target="_blank"
                      className="text-blue-500 underline"
                    >
                      (Click Here)
                    </a>{" "}
                    and capture the photo to upload
                  </PopoverContent>
                </Popover>
                <Button
                  type="button"
                  variant="link"
                  onClick={() =>
                    handleDownload(sampleImage, `sampleImage.jpeg`)
                  }
                  className="p-0 m-0"
                >
                  (Sample Image)
                </Button>
              </Label>
              <div className="border-2 border-dashed rounded-lg p-4 text-center">
                <Input
                  id="geo_tag"
                  type="file"
                  accept="image/*"
                  onChange={(e) =>
                    handleFileUpload("geo_tag", e.target.files[0])
                  }
                  className={errors.geo_tag ? "border-red-500" : ""}
                />

                <FilePreview
                  fieldName="geo_tag"
                  preview={filePreviews.geo_tag}
                  file={
                    formData.geo_tag instanceof File ? formData.geo_tag : null
                  }
                  existingFile={
                    typeof filePreviews.geo_tag === "string"
                      ? filePreviews.geo_tag
                      : null
                  }
                  onRemove={handleRemoveFile}
                />

                {errors.geo_tag && (
                  <p className="text-sm text-red-500">
                    {errors.geo_tag.message}
                  </p>
                )}

                <p className="ms-1 text-[12px] text-muted-foreground">
                  Supported only JPG, JPEG, PNG Files max size 2MB
                </p>
              </div>
            </div>

            {/* Main Facilities Proposed */}
            <div className="space-y-2">
              <Label htmlFor="main_facilities_proposed" required>
                Facilities to be Created
              </Label>
              <MultiSelect
                options={[
                  {
                    id: "Warping and sizing unit",
                    name: "Warping and sizing unit",
                  },
                  {
                    id: "Testing Lab",
                    name: "Testing Lab",
                  },
                  {
                    id: "Sample Production Centre",
                    name: "Sample Production Centre",
                  },
                  {
                    id: "Design Studio",
                    name: "Design Studio",
                  },
                ]}
                value={formData.main_facilities_proposed || []}
                onChange={(value) =>
                  handleInputChange("main_facilities_proposed", value)
                }
                placeholder="Select Tie-up Types"
              />
              {errors.main_facilities_proposed && (
                <p className="text-sm text-red-500">
                  {errors.main_facilities_proposed.message}
                </p>
              )}
            </div>

            {/* Cluster Details */}
            <div className="space-y-2">
              <Label
                htmlFor="cluster_details"
                className="flex items-center gap-4"
                required
              >
                <span>Is the proposed CFC located in cluster</span>
                <Popover>
                  <PopoverTrigger>
                    <HelpCircle className="text-primary" />
                  </PopoverTrigger>
                  <PopoverContent>
                    <span className="font-bold">Cluster</span>: If your shed is
                    located in a place where other Machinery sheds are in nearby
                    then select Cluster.
                  </PopoverContent>
                </Popover>
              </Label>
              <Select
                value={formData.cluster_details}
                onValueChange={(value) =>
                  handleInputChange("cluster_details", value)
                }
              >
                <SelectTrigger>
                  <SelectValue placeholder="Select Yes or No" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="yes">Yes</SelectItem>
                  <SelectItem value="no">No</SelectItem>
                </SelectContent>
              </Select>
              {errors.cluster_details && (
                <p className="text-sm text-red-500">
                  {errors.cluster_details.message}
                </p>
              )}
            </div>

            {formData?.cluster_details == "yes" && (
              <div className="space-y-2">
                <Label
                  htmlFor="about_cluster_copy"
                  className="flex items-center gap-4"
                  required
                >
                  <span>If yes, Describe the details about the cluster</span>
                  <Popover>
                    <PopoverTrigger>
                      <HelpCircle className="text-primary" />
                    </PopoverTrigger>
                    <PopoverContent>
                      <ol className="list-decimal pl-4 space-y-2 text-sm">
                        <li>
                          Basic data of cluster (Number of units, type of units
                          [Micro/Small/Medium], employment [direct/indirect],
                          turnover, exports, etc)
                        </li>
                        <li>
                          {" "}
                          Impact of the proposed CFC for the growth of the
                          cluster
                        </li>
                        <li>Details of forward & backward linkages</li>
                      </ol>
                    </PopoverContent>
                  </Popover>
                </Label>
                <div className="border-2 border-dashed rounded-lg p-4 text-center">
                  <Input
                    id="about_cluster_copy"
                    type="file"
                    accept=".pdf,.png,.jpg,.jpeg"
                    onChange={(e) =>
                      handleFileUpload("about_cluster_copy", e.target.files[0])
                    }
                    className={
                      errors.about_cluster_copy ? "border-red-500" : ""
                    }
                  />

                  <FilePreview
                    fieldName="about_cluster_copy"
                    preview={filePreviews.about_cluster_copy}
                    file={
                      formData.about_cluster_copy instanceof File
                        ? formData.about_cluster_copy
                        : null
                    }
                    existingFile={
                      typeof filePreviews.about_cluster_copy === "string"
                        ? filePreviews.about_cluster_copy
                        : null
                    }
                    onRemove={handleRemoveFile}
                  />

                  {errors.about_cluster_copy && (
                    <p className="text-sm text-red-500">
                      {errors.about_cluster_copy.message}
                    </p>
                  )}
                  <p className="ms-1 text-[12px] text-muted-foreground">
                    Supported only PDF, PNG, JPG, JPEG Files max size 10MB
                  </p>
                </div>
              </div>
            )}
          </div>

          <div className="flex justify-end mt-6 gap-2">
            <Button
              type="submit"
              disabled={isSubmitting}
              onClick={() => setButtonType("submit")}
              className="min-w-[150px]"
            >
              {isSubmitting && buttonType === "submit" ? (
                <>
                  <Loader2 className="h-4 w-4 animate-spin mr-2" />
                  Saving...
                </>
              ) : (
                "Save"
              )}
            </Button>
            <Button
              type="submit"
              onClick={() => setButtonType("next")}
              disabled={isSubmitting}
              className="min-w-[150px]"
            >
              {isSubmitting && buttonType === "next" ? (
                <>
                  <Loader2 className="h-4 w-4 animate-spin mr-2" />
                  Saving...
                </>
              ) : (
                "Save and Next"
              )}
            </Button>
          </div>
        </CardContent>
      </Card>
      <Dialog open={districtNoteOpen} onOpenChange={setDistrictNoteOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Important Note</DialogTitle>
          </DialogHeader>
          <div className="py-4">
            If You are from Edappadi and Sankagiri Taluk of Salem then please
            select the <b>Salem(Edappadi & Sankagiri Taluk)</b> from dropdown
          </div>
          <DialogFooter className="flex space-x-2">
            <Button
              variant="outline"
              onClick={() => setDistrictNoteOpen(false)}
            >
              OK
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </form>
  );
};

export default SpvDetails;
